import ufeApi from 'services/api/ufeApi';
import { gqlClient } from 'services/api/gql/gqlClient';
import { GetNcrOrderDetails } from 'constants/gql/queries/checkout/ncrOrderDetails.gql';
import Location from 'utils/Location';
import UrlUtils from 'utils/Url';
import OrderUtils from 'utils/Order';

// https://confluence.sephora.com/wiki/display/ILLUMINATE/Get+Order+Details+API
// https://confluence.sephora.com/wiki/display/ILLUMINATE/NCR+Order+Details+API

/**
 * Fetches order details for display on the order details page.
 * Routes to OIS GraphQL when isReshipOrder is true and OIS returns-migration flags are enabled,
 * otherwise falls back to legacy OXS REST endpoint.
 *
 * For OIS GraphQL requests, normalizes the response to backfill fields expected by downstream
 * Order.js utilities (shippingGroupType for getHardGoodShippingGroup(), items.items for aggregation).
 *
 * @param {string} orderId - The order ID to fetch details for
 * @param {string} [guestEmail=''] - Optional guest email for non-authenticated guests
 * @param {boolean} [isReshipOrder=false] - Whether this is a replacement/reship order
 * @param {boolean} [appendRequestOrigin=false] - Whether to append requestOrigin based on page context
 * @param {string} [originalOrderId=''] - Optional original order ID for NCR contexts
 * @returns {Promise<Object>} Order details object (normalized for both OIS and OXS paths)
 * @throws {Error} Throws when OIS response shape is unexpected or GQL client rejects
 * @throws {Object} Throws raw data object for OXS errors when response contains an errorCode
 */
const getOrderDetails = async (orderId, guestEmail = '', isReshipOrder = false, appendRequestOrigin = false, originalOrderId = '') => {
    const { requestOrigin } = UrlUtils.getParams();
    const resolvedOrigin =
        Location.isOrderDetailsPage() || appendRequestOrigin
            ? requestOrigin?.includes?.(OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_CONFIRMATION_PAGE)
                ? OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_CONFIRMATION_PAGE
                : OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_DETAILS_PAGE
            : '';

    // Route to OIS GraphQL for replacement orders when both flags are enabled
    const globalEnabled = Sephora?.configurationSettings?.oisMigration?.global?.isEnabled;
    const returnsEnabled = Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled;

    if (isReshipOrder && globalEnabled && returnsEnabled) {
        const data = await gqlClient.query({
            operationName: 'GetNcrOrderDetails',
            query: GetNcrOrderDetails,
            variables: {
                input: {
                    orderId,
                    isReshipOrder: true,
                    ...(originalOrderId && { originalOrderId }),
                    ...(resolvedOrigin && { requestOrigin: resolvedOrigin })
                }
            }
        });

        const result = data?.ncrOrderDetails;

        if (!result) {
            throw new Error('Get NCR order details: unexpected response shape');
        }

        // Normalize OIS response to match OXS shape expected by downstream Order.js utilities
        const entries = result.shippingGroups?.shippingGroupsEntries || [];

        // OIS returns sku.displayName; downstream components (e.g. ItemsInOrder, Accessibility.getImageAltText)
        // read sku.productName — backfill it from displayName when not already present.
        const normalizeItems = items =>
            (items || []).map(item => ({
                ...item,
                sku: { ...item.sku, productName: item.sku?.productName ?? item.sku?.displayName }
            }));

        // OIS returns uppercase snake_case group types; UFE utilities expect PascalCase (OXS format)
        const OIS_TO_UFE_GROUP_TYPE = {
            HARD_GOOD_SHIPPING_GROUP: OrderUtils.SHIPPING_GROUPS.HARD_GOOD,
            GIFT_CARD_SHIPPING_GROUP: OrderUtils.SHIPPING_GROUPS.GIFT,
            ELECTRONIC_SHIPPING_GROUP: OrderUtils.SHIPPING_GROUPS.ELECTRONIC,
            SAME_DAY_SHIPPING_GROUP: OrderUtils.SHIPPING_GROUPS.SAME_DAY,
            SDU_ELECTRONIC_SHIPPING_GROUP: OrderUtils.SHIPPING_GROUPS.SDU_ELECTRONIC
        };

        const normalizedEntries = entries.map(entry => {
            const groupType = OIS_TO_UFE_GROUP_TYPE[entry.shippingGroupType] || entry.shippingGroupType || OrderUtils.SHIPPING_GROUPS.HARD_GOOD;

            return {
                ...entry,
                shippingGroupType: groupType,
                shippingGroup: {
                    ...entry.shippingGroup,
                    shippingGroupType: groupType,
                    items: normalizeItems(entry.shippingGroup?.items)
                }
            };
        });

        const allItems = normalizedEntries.flatMap(entry => entry.shippingGroup?.items || []);

        // Derive itemsByBasket grouping to match OXS structure consumed by Order Confirmation,
        // Klarna, and OrderCancelationModal. Maps SameDayShippingGroup → 'SameDay' basket;
        // all other group types fall through to the 'ShipToHome' (standard) basket.
        const SHIPPING_GROUP_TO_BASKET_TYPE = {
            [OrderUtils.SHIPPING_GROUPS.SAME_DAY]: 'SameDay'
        };
        const DEFAULT_BASKET_TYPE = 'ShipToHome';

        const itemsByBasket = Object.values(
            normalizedEntries.reduce((acc, entry) => {
                const basketType = SHIPPING_GROUP_TO_BASKET_TYPE[entry.shippingGroupType] || DEFAULT_BASKET_TYPE;

                if (!acc[basketType]) {
                    acc[basketType] = { basketType, items: [] };
                }

                acc[basketType].items.push(...(entry.shippingGroup?.items || []));

                return acc;
            }, { [DEFAULT_BASKET_TYPE]: { basketType: DEFAULT_BASKET_TYPE, items: [] } })
        );

        return {
            ...result,
            items: { ...result.items, items: allItems, itemsByBasket },
            shippingGroups: {
                ...result.shippingGroups,
                shippingGroupsEntries: normalizedEntries
            }
        };
    }

    // OXS REST fallback path
    const url = [
        `/api/checkout/orders/${orderId}?includeShippingItems=true&includeProfileFlags=true`,
        originalOrderId ? `&originalOrderId=${originalOrderId}` : '',
        guestEmail ? `&guestEmail=${guestEmail}` : '',
        resolvedOrigin ? `&requestOrigin=${resolvedOrigin}` : '',
        isReshipOrder ? `&isReshipOrder=${isReshipOrder}` : ''
    ].join('');

    const data = await ufeApi.makeRequest(url, { method: 'GET' });

    if (data?.errorCode) {
        throw data;
    }

    return data;
};

export default getOrderDetails;
